import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { fetchChapter } from "../../api/BibleApi";
import { chapterPath, getPassageId } from "../../utils/paths";
import ChapterLayout from "./ChapterLayout/ChapterLayout";
import ChapterAudio from "./ChapterAudio/ChapterAudio";
import withTracker from "../../utils/withTracker";
import ChapterMeta from "./ChapterMeta";
import { withBible } from "../Bible/BibleContainer";
import getVerseIdsFromPassageId from "../../utils/getVerseIdsFromPassageId";

// import { getGlobalFumsTracker } from "../../api/FumsReporter";
// // const fumsTracker = getGlobalFumsTracker();
// fumsTracker.start();

const ChapterContext = React.createContext();

class ChapterContainer extends Component {
  static Consumer = ChapterContext.Consumer;

  static propTypes = {
    // bibleId: PropTypes.string.isRequired,
    // parallelBibleIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        bibleId: PropTypes.string.isRequired,
        chapterId: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired
    }).isRequired,
    bible: PropTypes.shape({
      currentBibleId: PropTypes.string,
      parallelBibleIds: PropTypes.arrayOf(PropTypes.string),
      getCurrentBible: PropTypes.func.isRequired,
      biblesById: PropTypes.shape({}).isRequired
    }).isRequired
  };

  UNSAFE_componentWillMount() {
    const { chapterId } = this.props.match.params;
    const passageId = getPassageId({ location: this.props.location });
    const { currentBibleId, parallelBibleIds } = this.props.bible;
    const currentChapterUid = this.getChapterUid({
      bibleId: currentBibleId,
      chapterId
    });

    if (currentBibleId && chapterId) {
      this.setState({
        currentChapterId: chapterId,
        currentChapterUid,
        passageId
      });
      this.loadContent({
        bibleId: currentBibleId,
        chapterId,
        parallelBibleIds
      });
    }

    if (passageId) {
      const matchedVerseOrgIds = getVerseIdsFromPassageId(passageId);
      this.setState({
        matchedVerseOrgIds
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { chapterId } = this.props.match.params;
    const {
      currentBibleId,
      parallelBibleIds,
      getCurrentBible
    } = this.props.bible;

    const currentChapterUid = this.getChapterUid({
      bibleId: currentBibleId,
      chapterId,
      parallelBibleIds
    });

    const currentBible = getCurrentBible();

    if (currentBible) {
      const { orderedChapterIds, books } = currentBible;

      if (!this.state.isLoading()) {
        // Check for non-existent chapter and redirect
        if (books) {
          const indexOfCurrentChapter = books.findIndex(
            book => book.id === chapterId.split(".")[0]
          );
          if (indexOfCurrentChapter === -1) {
            return this.getDefaultChapter(currentBible);
          }
        }

        if (
          currentBibleId !== prevProps.bible.currentBibleId ||
          chapterId !== prevProps.match.params.chapterId ||
          prevProps.bible.parallelBibleIds !== parallelBibleIds
        ) {
          this.setState({ currentChapterId: chapterId, currentChapterUid });

          this.loadContent({
            bibleId: currentBibleId,
            chapterId,
            parallelBibleIds,
            orderedChapterIds
          });
        } else if (
          this.state.chapterPanelIds.length <= 1 &&
          orderedChapterIds
        ) {
          // build chapterPanelIds if we haven't already
          const currentChapter = this.getCurrentChapter();
          const chapterIdIndex =
            currentChapter && currentChapter.orderedIndex
              ? currentChapter.orderedIndex
              : this.getOrderedIndex({
                  chapter: currentChapter,
                  orderedChapterIds
                });

          if (currentChapter && chapterIdIndex) {
            this.setState(prevState => ({
              chapters: {
                ...prevState.chapters,
                [currentChapterUid]: {
                  ...currentChapter,
                  orderedIndex: chapterIdIndex
                }
              }
            }));
          }

          if (chapterIdIndex && chapterIdIndex !== -1) {
            const chapterPanelIds = this.buildChapterPanels({
              chapterIdIndex,
              orderedChapterIds
            });

            this.setState({ chapterPanelIds });

            this.preFetchChapters({
              bibleId: currentBibleId,
              parallelBibleIds,
              chapterIdIndex,
              orderedChapterIds
            });
          }
        }
      }
    }
  }

  buildChapterPanels({ chapterIdIndex, orderedChapterIds }) {
    // 10 before, 10 after
    return orderedChapterIds.slice(
      Math.max(chapterIdIndex - 10, 0),
      chapterIdIndex + 11
    );
  }

  preFetchChapters({
    bibleId,
    parallelBibleIds,
    chapterIdIndex,
    orderedChapterIds
  }) {
    // load 1 before and 1 after the current chapter
    orderedChapterIds
      .slice(chapterIdIndex - 1, chapterIdIndex + 2)
      .filter((id, index) => index !== chapterIdIndex)
      .forEach(chapterPanelId =>
        this.loadChapter({
          bibleId,
          chapterId: chapterPanelId,
          parallelBibleIds
        })
      );
  }

  loadContent({ bibleId, chapterId, parallelBibleIds, orderedChapterIds }) {
    this.loadChapter({
      bibleId,
      chapterId,
      parallelBibleIds,
      orderedChapterIds
    }).then(chapter => {
      let chapterPanelIds = [chapterId];

      if (chapter && orderedChapterIds && orderedChapterIds.length > 0) {
        const chapterIdIndex = orderedChapterIds.indexOf(chapterId);
        chapterPanelIds = this.buildChapterPanels({
          chapterIdIndex,
          orderedChapterIds
        });

        this.preFetchChapters({
          bibleId,
          parallelBibleIds,
          chapterIdIndex,
          orderedChapterIds
        });
      }

      if (process.env.REACT_APP_ENABLE_FUMS && chapter && chapter.fumsId) {
        // fumsTracker.record(chapter.fumsId);
      }
      this.setState({ chapterPanelIds });
    });
  }

  getOrderedIndex({ chapter, orderedChapterIds }) {
    if (chapter) {
      const { next, previous, bibleId } = chapter;

      if (next) {
        const nextChapter = this.getChapter({ bibleId, chapterId: next.id });
        if (nextChapter && nextChapter.orderedIndex) {
          return nextChapter.orderedIndex - 1;
        }
      }

      if (previous) {
        const prevChapter = this.getChapter({
          bibleId,
          chapterId: previous.id
        });
        if (prevChapter && prevChapter.orderedIndex) {
          return prevChapter.orderedIndex + 1;
        }
      }

      if (orderedChapterIds) {
        const foundIndex = orderedChapterIds.indexOf(chapter.id);
        return foundIndex !== -1 ? foundIndex : undefined;
      }
    }
    return undefined;
  }

  loadChapter({ bibleId, chapterId, parallelBibleIds, orderedChapterIds }) {
    const chapterUid = this.getChapterUid({
      bibleId,
      chapterId
    });
    const cachedChapter = this.state.chapters[chapterUid];

    if (cachedChapter) {
      let cacheDirty = false;

      cacheDirty =
        cachedChapter.parallelBibleIds.join(",") !== parallelBibleIds.join(",");

      if (!cacheDirty) {
        const chapter = this.state.chapters[chapterUid];
        return Promise.resolve(chapter);
      }
    }

    if (!this.isLoading({ chapterUid })) {
      this.setState({
        chapterStatusById: {
          ...this.state.chapterStatusById,
          [chapterUid]: {
            isLoading: true
          }
        }
      });

      return fetchChapter({
        bibleId,
        chapterId,
        footnotes: true,
        parallels: parallelBibleIds
      })
        .then(chapter => {
          if (!chapter) {
            throw new Error("No chapter returned from API fetch");
          }

          const orderedIndex = this.getOrderedIndex({
            chapter,
            orderedChapterIds
          });

          this.setState(prevState => ({
            chapters: {
              ...prevState.chapters,
              [chapterUid]: {
                ...chapter,
                parallelBibleIds,
                orderedIndex
              }
            },
            chapterStatusById: {
              ...prevState.chapterStatusById,
              [chapterUid]: {
                isLoading: false,
                failedFetch: false
              }
            }
          }));

          return chapter;
        })
        .catch(err => {
          console.log("err:", err);

          const nullChapter = {
            bibleId,
            reference: "--- ",
            isNullChapter: true,
            copyright: "",
            parallelBibleIds
          };

          this.setState(prevState => ({
            chapters: {
              ...prevState.chapters,
              [chapterUid]: nullChapter
            },
            chapterStatusById: {
              ...prevState.chapterStatusById,
              [chapterUid]: {
                isLoading: false,
                failedFetch: true
              }
            }
          }));
          // Trigger logo display here
          return nullChapter;
        });
    }
    return Promise.resolve();
  }

  handleChapterSelect = chapter => {
    if (chapter) {
      const chapterLink = chapterPath({
        bibleId: chapter.bibleId,
        chapterId: chapter.id,
        location: this.props.location
      });
      this.props.history.push(chapterLink);
    }
  };

  handleVerseHover = verseOrgIds => {
    this.setState({ highlightedVerseOrgIds: verseOrgIds });
  };

  handleVerseUnhover = () => {
    this.setState({ highlightedVerseOrgIds: [] });
  };

  handleVerseSelect = verseOrgIds => {
    const { selectedVerseOrgIds } = this.state;

    // console.log("verseOrgIds:", verseOrgIds);
    // console.log("selectedVerseOrgIds:", selectedVerseOrgIds);

    // FOR MULTI-SELECTED VERSES

    // if (
    //   verseOrgIds.length > 0 &&
    //   selectedVerseOrgIds.includes(verseOrgIds[0])
    // ) {
    //   const diffOfVerseOrgIds = verseOrgIds
    //     .filter(x => !selectedVerseOrgIds.includes(x))
    //     .concat(selectedVerseOrgIds.filter(x => !verseOrgIds.includes(x)));
    //   this.setState({ selectedVerseOrgIds: diffOfVerseOrgIds });
    // } else {
    //   const newVerseOrgIds = [...selectedVerseOrgIds, ...verseOrgIds];
    //   this.setState({ selectedVerseOrgIds: newVerseOrgIds });
    // }
    if (verseOrgIds.length > 0)
      if (selectedVerseOrgIds === verseOrgIds)
        this.setState({ selectedVerseOrgIds: [] });
      else this.setState({ selectedVerseOrgIds: verseOrgIds });
  };

  handleRefClick = id => {
    // console.log("handleRefClick: id: ", id);
    if (id) {
      const [bookId, chapterNum] = id.split(".");
      const chapterId = [bookId, chapterNum].join(".");
      const chapterLink = chapterPath({
        bibleId: this.props.bible.currentBibleId,
        chapterId,
        passageId: id,
        location: this.props.location
      });
      this.props.history.push(chapterLink);
    }
  };

  handleChapterSwipe = targetChapterId => {
    if (targetChapterId) {
      this.handleChapterSelect({
        bibleId: this.props.bible.currentBibleId,
        id: targetChapterId
      });
    }
  };

  isLoading = ({
    chapterUid,
    bibleId = this.props.bible.currentBibleId,
    chapterId = this.state.currentChapterId
  } = {}) => {
    const requestedChapterUid =
      chapterUid || this.getChapterUid({ bibleId, chapterId });
    const chapterStatus = this.state.chapterStatusById[requestedChapterUid];
    return chapterStatus !== undefined && chapterStatus.isLoading;
  };

  getDefaultChapter = nextBible => {
    if (nextBible && nextBible.books && nextBible.books.length > 0) {
      const filteredChapters = nextBible.books[0].chapters.filter(
        chapter => chapter.number !== "intro"
      );
      const defaultChapter = filteredChapters[0];
      this.handleChapterSelect(defaultChapter);
    }
  };

  getChapterUid = ({ bibleId, chapterId }) => [bibleId, chapterId].join(":");

  getCurrentChapter = () =>
    this.getChapter({
      chapterUid: this.state.currentChapterUid
    });

  getChapterReference = ({ bibleId, chapterId, limit }) => {
    const chapter = this.getChapter({ bibleId, chapterId });
    const bible = this.props.bible.biblesById[bibleId];

    const [bookId, chapterNumber] = chapterId.split(".");

    let chapterReference = `${bookId} ${chapterNumber}`;
    const chapterBook = bible.books.find(book => book.id === bookId);

    if (chapter) {
      chapterReference = chapter.reference;
    } else if (bible && bible.books) {
      // chapterBook = bible.books.find(book => book.id === bookId);
      chapterReference =
        chapterBook && chapterBook.name
          ? `${chapterBook.name} ${chapterNumber}`
          : chapterReference;
    }

    if (limit && chapterReference.length > limit) {
      chapterReference =
        chapterBook && chapterBook.abbreviation
          ? `${chapterBook.abbreviation} ${chapterNumber}`
          : `${chapterReference.slice(0, limit - 3)}...`;
    }

    return chapterReference;
  };

  getChapter = ({
    chapterUid,
    bibleId = this.props.bible.currentBibleId,
    chapterId = this.state.currentChapterId
  }) => {
    const requestedChapterUid =
      chapterUid || this.getChapterUid({ bibleId, chapterId });

    const chapter = this.state.chapters[requestedChapterUid];

    // fallback to chapter without parallels if paralles are loading
    return chapter;
  };

  state = {
    chapters: {},
    orderedChapterIds: [],
    chapterPanelIds: [],
    currentChapterId: null,
    currentChapterUid: null,
    isLoading: this.isLoading,
    onChapterSelect: this.handleChapterSelect,
    onVerseHover: this.handleVerseHover,
    onVerseUnhover: this.handleVerseUnhover,
    onVerseSelect: this.handleVerseSelect,
    onRefClick: this.handleRefClick,
    onChapterSwipe: this.handleChapterSwipe,
    getChapter: this.getChapter,
    getCurrentChapter: this.getCurrentChapter,
    getChapterReference: this.getChapterReference,
    highlightedVerseOrgIds: [],
    matchedVerseOrgIds: [],
    passageId: null,
    chapterStatusById: {},
    selectedVerseOrgIds: []
  };

  render() {
    const chapter = this.getCurrentChapter();
    const currentBible = this.props.bible.getCurrentBible();
    return (
      <ChapterContext.Provider value={this.state}>
        <ChapterAudio bible={currentBible} chapter={chapter}>
          <ChapterMeta bible={currentBible} chapter={chapter} />
          <ChapterLayout />
        </ChapterAudio>
      </ChapterContext.Provider>
    );
  }
}

export default withBible(withRouter(withTracker(ChapterContainer)));

export function withChapter(WrappedComponent) {
  return function ComponentWithChapter(props) {
    return (
      <ChapterContainer.Consumer>
        {context => <WrappedComponent {...props} chapter={context} />}
      </ChapterContainer.Consumer>
    );
  };
}
